import "./calendar.css";
import type { ConnectionView, EventUpdate, EventView, ReceivedShareView } from "@confluence/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { patch } from "../api";
import { canWrite, DeleteBar, EventEditor, writeErrorText, type EditorTarget } from "./editor";
import { useI18n, type Translate } from "../i18n";
import { useMe } from "../session";
import { useCalendarIndex, useEvents, useLiveUpdates, type CalendarInfo } from "./data";
import { dayFmt, eventWhen, monthRangeTitle, useMediaQuery, useToday } from "./format";
import { addDays, addMonths, isDayKey, isoWeek, minutesIn, startOfMonth, startOfWeek, todayIn, type DayKey } from "./time";
import { SearchBox } from "./search";
import { AgendaStrip, AgendaView, BoxesView, MonthView, WeekView } from "./views";

export const VIEWS = ["day", "threeDays", "week", "month", "agenda", "boxes"] as const;
export type View = (typeof VIEWS)[number];
export interface CalendarSearch {
  view?: View;
  date?: DayKey;
}
export const validateCalendarSearch = (s: Record<string, unknown>): CalendarSearch => ({
  view: (VIEWS as readonly unknown[]).includes(s.view) ? (s.view as View) : undefined,
  date: isDayKey(s.date) ? s.date : undefined,
});

// d jour, 3 trois jours, w semaine, m mois, l ou a agenda (liste), b cases.
const VIEW_KEYS: Record<string, View> = { d: "day", "3": "threeDays", w: "week", m: "month", l: "agenda", a: "agenda", b: "boxes" };

/** Jours couverts par une vue autour de la date choisie. */
function rangeFor(view: View, anchor: DayKey, weekStart: number): DayKey[] {
  if (view === "day") return [anchor];
  if (view === "threeDays") return [anchor, addDays(anchor, 1), addDays(anchor, 2)];
  const first = view === "month" ? startOfWeek(startOfMonth(anchor), weekStart) : startOfWeek(anchor, weekStart);
  return Array.from({ length: view === "month" ? 42 : 7 }, (_, i) => addDays(first, i));
}

const STEP_DAYS: Partial<Record<View, number>> = { day: 1, threeDays: 3 };
const step = (view: View, anchor: DayKey, dir: 1 | -1) => (view === "month" ? addMonths(anchor, dir) : addDays(anchor, (STEP_DAYS[view] ?? 7) * dir));

export function CalendarPage() {
  const { t, locale, errorText } = useI18n();
  const me = useMe().data!;
  const tz = me.timezone;
  const search = useSearch({ strict: false }) as CalendarSearch;
  const navigate = useNavigate();
  const narrow = useMediaQuery("(max-width: 760px)");
  const today = useToday(tz);
  // Vue au démarrage : celle des préférences, sinon les cases sur téléphone et la semaine ailleurs.
  const view: View = search.view ?? me.prefs.startView ?? (narrow ? "boxes" : "week");
  const weekNumbers = me.prefs.weekNumbers !== false;
  const searchRef = useRef<HTMLInputElement>(null);
  const anchor = search.date ?? today;
  const [selected, setSelected] = useState<EventView | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { connections, shares, index, shareIds } = useCalendarIndex();
  const qc = useQueryClient();
  useLiveUpdates();
  const hasWritable = [...index.values()].some(canWrite);

  useEffect(() => {
    if (!toast) return;
    const h = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(h);
  }, [toast]);

  /** Nouvel événement : au créneau choisi, sinon à la prochaine demi-heure. */
  const create = (date?: DayKey, minutes?: number | null) => {
    if (!hasWritable) return;
    const now = minutesIn(Date.now(), tz);
    const day = date ?? (anchor === today ? today : anchor);
    const next = day === todayIn(tz) ? Math.min(Math.ceil((now + 1) / 30) * 30, 23 * 60) : 9 * 60;
    setSelected(null);
    setEditor({ kind: "new", date: day, minutes: minutes === undefined || minutes === null ? next : minutes });
  };

  // Glisser-déposer : l'affichage bouge tout de suite, puis se cale sur la réponse du fournisseur.
  const moveEvent = async (ev: EventView, start: Date, end: Date) => {
    qc.setQueriesData<EventView[]>({ queryKey: ["events"] }, (list) =>
      Array.isArray(list) ? list.map((x) => (x.id === ev.id ? { ...x, start: start.toISOString(), end: end.toISOString() } : x)) : list,
    );
    try {
      const body: EventUpdate = { allDay: false, start: start.toISOString(), end: end.toISOString(), timezone: tz, scope: "this" };
      await patch(`/api/events/${ev.id}`, body);
      setToast(t("ev.saved"));
    } catch (err) {
      setToast(`${t("ev.moveFailed")} ${writeErrorText(err, errorText)}`);
    } finally {
      void qc.invalidateQueries({ queryKey: ["events"] });
    }
  };

  const go = (next: CalendarSearch) => {
    void navigate({ to: "/", search: { view: search.view, date: search.date, ...next }, replace: true });
    setSideOpen(false);
  };
  const goToday = () => go({ date: undefined });
  const move = (dir: 1 | -1) => go({ date: step(view, anchor, dir) });

  const days = useMemo(() => rangeFor(view, anchor, me.weekStart), [view, anchor, me.weekStart]);
  const first = days[0]!;
  const end = addDays(days[days.length - 1]!, 1);
  const span = daysSpan(view);
  const query = useEvents(first, end, tz, shareIds, {
    prev: [addDays(first, -span), first],
    next: [end, addDays(end, span)],
  });
  // Un agenda masqué disparaît tout de suite, sans attendre la réponse du serveur.
  const events = useMemo(() => (query.data ?? []).filter((ev) => index.get(ev.calendarId)?.shown !== false), [query.data, index]);

  // Plage synchronisée : 6 mois en arrière, 18 mois en avant.
  const bounds: [DayKey, DayKey] = [addDays(today, -183), addDays(today, 548)];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || selected || editor) return;
      const target = e.target;
      if (target instanceof Element && target.closest("input, textarea, select, [contenteditable], dialog[open]")) return;
      const key = e.key.toLowerCase();
      if (key === "t") goToday();
      else if (key === "n") create();
      else if (e.key === "/") searchRef.current?.focus();
      else if (e.key === "ArrowLeft" || key === "k") move(-1);
      else if (e.key === "ArrowRight" || key === "j") move(1);
      else if (VIEW_KEYS[key]) go({ view: VIEW_KEYS[key] });
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const heading =
    view === "month" || view === "agenda"
      ? dayFmt(anchor, locale, { month: "long", year: "numeric" })
      : view === "day"
        ? dayFmt(anchor, locale, { weekday: "long", day: "numeric", month: "long" })
        : monthRangeTitle(first, days[days.length - 1]!, locale);
  const props = {
    tz,
    today,
    calendars: index,
    onOpen: setSelected,
    onPickDay: (date: DayKey, v?: View) => go({ date, view: v ?? view }),
    onCreate: hasWritable ? create : undefined,
  };

  return (
    <div className="cal-layout">
      <aside className={`cal-side${sideOpen ? " is-open" : ""}`} aria-label={t("cal.calendars")}>
        {hasWritable && (
          <button className="btn btn-primary btn-block new-btn" onClick={() => create()}>
            <Icon d="M12 5v14M5 12h14" /> {t("ev.new")}
          </button>
        )}
        <MiniMonth anchor={anchor} today={today} selected={view === "month" ? null : days} weekStart={me.weekStart} onPick={(date) => go({ date })} />
        <CalendarList connections={connections.data ?? []} shared={shares.data?.withMe ?? []} />
        <p className="cal-keys muted">{t("cal.shortcuts")}</p>
      </aside>
      {sideOpen && <button className="cal-scrim" aria-label={t("ev.close")} onClick={() => setSideOpen(false)} />}

      <section className="cal-main">
        <header className="viewhead">
          <button className="icon-btn side-toggle" aria-label={t("cal.toggleSidebar")} aria-expanded={sideOpen} onClick={() => setSideOpen((o) => !o)}>
            <Icon d="M4 6h16M4 12h16M4 18h16" />
          </button>
          <h1>
            {heading}
            {weekNumbers && view !== "month" && view !== "agenda" && <small className="tnum">{t("cal.weekNumber", { n: String(isoWeek(first)) })}</small>}
          </h1>
          <div className="nav">
            <button className="icon-btn" aria-label={t("cal.prev")} onClick={() => move(-1)}>
              <Icon d="M15 18l-6-6 6-6" />
            </button>
            <button className="btn btn-sm" onClick={goToday}>
              {t("common.today")}
            </button>
            <button className="icon-btn" aria-label={t("cal.next")} onClick={() => move(1)}>
              <Icon d="M9 18l6-6-6-6" />
            </button>
          </div>
          <SearchBox inputRef={searchRef} tz={tz} index={index} onOpen={(ev) => setSelected(ev)} onGo={(date) => go({ date })} />
          {hasWritable && (
            <button className="icon-btn add-btn" aria-label={t("ev.new")} onClick={() => create()}>
              <Icon d="M12 5v14M5 12h14" />
            </button>
          )}
          <div className="seg" role="group" aria-label={t("view.label")}>
            {VIEWS.map((v) => (
              <button key={v} aria-pressed={view === v} onClick={() => go({ view: v })}>
                {t(`view.${v}`)}
              </button>
            ))}
          </div>
        </header>

        {view === "agenda" && <AgendaStrip anchor={anchor} today={today} tz={tz} weekStart={me.weekStart} shareIds={shareIds} calendars={index} onPick={(date) => go({ date })} />}
        <div className={`cal-scroll view-${view === "day" || view === "threeDays" ? "week" : view}`} aria-busy={query.isFetching}>
          {(view === "week" || view === "day" || view === "threeDays") && (
            <WeekView {...props} days={days} events={events} weekNumbers={weekNumbers} canEdit={(ev) => canWrite(index.get(ev.calendarId)) && !ev.block} onMove={moveEvent} />
          )}
          {view === "month" && <MonthView {...props} anchor={anchor} days={days} events={events} />}
          {view === "agenda" && <AgendaView key={anchor} {...props} anchor={anchor} bounds={bounds} shareIds={shareIds} />}
          {view === "boxes" && <BoxesView {...props} days={days} events={events} onSwipe={move} />}
        </div>
      </section>

      <EventDialog
        ev={selected}
        cal={selected ? index.get(selected.calendarId) : undefined}
        tz={tz}
        onClose={() => setSelected(null)}
        onEdit={(ev) => {
          setSelected(null);
          setEditor({ kind: "edit", ev });
        }}
        onDeleted={(message) => {
          setSelected(null);
          setToast(message);
        }}
      />
      {editor && (
        <EventEditor
          key={editor.kind === "edit" ? editor.ev.id : `${editor.date}-${editor.minutes}`}
          target={editor}
          calendars={index}
          tz={tz}
          onClose={() => setEditor(null)}
          onDone={(message) => {
            setEditor(null);
            setToast(message);
          }}
        />
      )}
      <div className="toast-zone" role="status" aria-live="polite">
        {toast && <p className="toast">{toast}</p>}
      </div>
    </div>
  );
}

const daysSpan = (view: View) => (view === "month" ? 42 : view === "day" ? 1 : view === "threeDays" ? 3 : 7);

function Icon({ d }: { d: string }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// ───── Mini-calendrier ─────

function MiniMonth({ anchor, today, selected, weekStart, onPick }: { anchor: DayKey; today: DayKey; selected: DayKey[] | null; weekStart: number; onPick: (d: DayKey) => void }) {
  const { t, locale } = useI18n();
  const [month, setMonth] = useState(startOfMonth(anchor));
  useEffect(() => setMonth(startOfMonth(anchor)), [anchor]);
  const first = startOfWeek(month, weekStart);
  const days = Array.from({ length: 42 }, (_, i) => addDays(first, i));
  const inRange = (k: DayKey) => !!selected && k >= selected[0]! && k <= selected[selected.length - 1]!;
  return (
    <div className="mini">
      <div className="mini-head">
        <strong>{dayFmt(month, locale, { month: "long", year: "numeric" })}</strong>
        <span className="nav">
          <button className="icon-btn icon-sm" aria-label={t("cal.prev")} onClick={() => setMonth(addMonths(month, -1))}>
            <Icon d="M15 18l-6-6 6-6" />
          </button>
          <button className="icon-btn icon-sm" aria-label={t("cal.next")} onClick={() => setMonth(addMonths(month, 1))}>
            <Icon d="M9 18l6-6-6-6" />
          </button>
        </span>
      </div>
      <div className="mini-grid">
        {days.slice(0, 7).map((k) => (
          <span key={k} className="dow" aria-hidden="true">
            {dayFmt(k, locale, { weekday: "narrow" })}
          </span>
        ))}
        {days.map((k) => (
          <button
            key={k}
            className={[k.slice(0, 7) !== month.slice(0, 7) && "out", inRange(k) && "in-week", k === today && "today", k === anchor && "sel"].filter(Boolean).join(" ")}
            aria-label={dayFmt(k, locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            aria-current={k === today ? "date" : undefined}
            onClick={() => onPick(k)}
          >
            {Number(k.slice(8))}
          </button>
        ))}
      </div>
    </div>
  );
}

// ───── Liste des agendas ─────

function CalendarList({ connections, shared }: { connections: ConnectionView[]; shared: ReceivedShareView[] }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toggleShare = async (id: string, shown: boolean) => {
    qc.setQueryData<{ withMe: ReceivedShareView[] }>(["shares"], (d) => d && { ...d, withMe: d.withMe.map((s) => (s.id === id ? { ...s, hidden: !shown } : s)) });
    await patch(`/api/shares/${id}/viewer`, { hidden: !shown }).finally(() => void qc.invalidateQueries({ queryKey: ["shares"] }));
  };
  const toggle = async (id: string, shown: boolean) => {
    qc.setQueryData<ConnectionView[]>(["connections"], (list) =>
      list?.map((c) => ({ ...c, calendars: c.calendars.map((k) => (k.id === id ? { ...k, shown } : k)) })),
    );
    await patch(`/api/calendars/${id}`, { shown }).finally(() => {
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["events"] });
    });
  };
  return (
    <div className="cal-groups">
      <h2 className="side-title">{t("cal.calendars")}</h2>
      {connections.map((c) => (
        <div key={c.id} className="cal-group">
          <p className={`conn${c.status === "ok" ? "" : " is-err"}`} title={c.label}>
            <span className="st" aria-hidden="true" />
            <span className="addr">{c.label}</span>
          </p>
          {c.status !== "ok" && (
            <a className="reconnect" href="/settings?section=calendars">
              {t(c.status === "reauth" ? "cal.status.reauth" : "home.attention")}
            </a>
          )}
          {c.calendars.map((cal) => (
            <label key={cal.id} className="cal-item" style={{ ["--cal" as string]: cal.color }}>
              <input type="checkbox" checked={cal.shown} onChange={(e) => void toggle(cal.id, e.target.checked)} />
              <span className="box" aria-hidden="true" />
              <span className="nm">{cal.name}</span>
            </label>
          ))}
        </div>
      ))}
      <a className="side-link" href="/settings?section=calendars">
        {t("home.manage")}
      </a>
      {shared.length > 0 && (
        <>
          <h2 className="side-title side-title-gap">{t("share.withMe")}</h2>
          {shared.map((s) => (
            <label key={s.id} className="cal-item" style={{ ["--cal" as string]: s.color }} title={s.owner.email}>
              <input type="checkbox" checked={!s.hidden} onChange={(e) => void toggleShare(s.id, e.target.checked)} />
              <span className="box" aria-hidden="true" />
              <span className="nm">{s.label || s.owner.name}</span>
            </label>
          ))}
        </>
      )}
      <a className="side-link" href="/settings?section=sharing">
        {t("share.manage")}
      </a>
    </div>
  );
}

// ───── Fiche d'un événement ─────

function EventDialog({
  ev,
  cal,
  tz,
  onClose,
  onEdit,
  onDeleted,
}: {
  ev: EventView | null;
  cal: CalendarInfo | undefined;
  tz: string;
  onClose: () => void;
  onEdit: (ev: EventView) => void;
  onDeleted: (message: string) => void;
}) {
  const { t, locale } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    setDeleting(false);
    if (ev && !d.open) d.showModal();
    if (!ev && d.open) d.close();
  }, [ev]);
  const editable = canWrite(cal);

  const tags = ev
    ? [
        ev.recurring && t("ev.recurring"),
        ev.status === "tentative" && t("ev.tentative"),
        ev.transparency === "transparent" && t("ev.free"),
        ev.isPrivate && t("ev.private"),
        ev.declined && t("ev.declined"),
      ].filter((x): x is string => !!x)
    : [];
  const locationIsLink = !!ev?.location && /^https?:\/\//i.test(ev.location);

  return (
    <dialog
      ref={ref}
      className="ev-dialog"
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      aria-labelledby="ev-dialog-title"
      style={{ ["--cal" as string]: cal?.color ?? "var(--accent)" }}
    >
      {ev && (
        <div className="ev-sheet">
          <div className="ev-top">
            <span className="ev-swatch" aria-hidden="true" />
            <h2 id="ev-dialog-title">{ev.busy ? t("share.busy") : ev.title || t("cal.noTitle")}</h2>
            <button className="icon-btn" aria-label={t("ev.close")} onClick={onClose} autoFocus>
              <Icon d="M6 6l12 12M18 6L6 18" />
            </button>
          </div>
          <p className="ev-when">{eventWhen(ev.start, ev.end, ev.allDay, locale, tz)}</p>
          {tags.length > 0 && (
            <p className="ev-tags">
              {tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </p>
          )}
          {ev.meetingUrl && (
            <a className="btn btn-primary ev-join" href={ev.meetingUrl} target="_blank" rel="noreferrer">
              {t("common.join")}
            </a>
          )}
          <dl className="ev-facts">
            {ev.location && (
              <Fact label={t("ev.location")}>
                {locationIsLink ? (
                  <Linkified text={ev.location} />
                ) : (
                  <>
                    {ev.location}{" "}
                    <a href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(ev.location)}`} target="_blank" rel="noreferrer">
                      {t("ev.openMap")}
                    </a>
                  </>
                )}
              </Fact>
            )}
            {cal && (
              <Fact label={t("cal.calendar")}>
                {cal.name} <span className="muted">· {cal.connection.label}</span>
              </Fact>
            )}
            {ev.attendees.length > 0 && (
              <Fact label={t("ev.guests")}>
                <ul className="ev-guests">
                  {ev.attendees.map((a) => (
                    <li key={a.email}>
                      <span>{a.name || a.email}</span>
                      <small className="muted">{guestStatus(a, t)}</small>
                    </li>
                  ))}
                </ul>
              </Fact>
            )}
            {ev.description && (
              <Fact label={t("ev.notes")}>
                <div className="ev-notes">
                  <Linkified text={plainText(ev.description)} />
                </div>
              </Fact>
            )}
          </dl>
          {editable ? (
            deleting ? (
              <DeleteBar ev={ev} onDone={onDeleted} onCancel={() => setDeleting(false)} />
            ) : (
              <div className="ev-actions">
                <button className="btn btn-sm" onClick={() => setDeleting(true)}>
                  {t("ev.delete")}
                </button>
                <button className="btn btn-sm btn-primary" onClick={() => onEdit(ev)}>
                  {t("ev.edit")}
                </button>
              </div>
            )
          ) : (
            <p className="muted ev-foot">{t("ev.cannotEdit")}</p>
          )}
        </div>
      )}
    </dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ev-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function guestStatus(a: EventView["attendees"][number], t: Translate) {
  const parts = [a.organizer && t("ev.organizer"), a.status && t(`ev.status.${a.status}`)].filter(Boolean);
  return parts.join(" · ");
}

/** Les descriptions Google et Microsoft peuvent contenir du HTML : on n'en garde que le texte. */
function plainText(s: string): string {
  if (!/<[a-z][\s\S]*>/i.test(s)) return s.trim();
  const html = s.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h\d)>/gi, "\n");
  const text = new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s<>"]+)/g);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <a key={i} href={p} target="_blank" rel="noreferrer">
            {p}
          </a>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}
